// /pair + /pair/invite — dispatched routes per W2 §4.3 step-2.
//
// `/pair`        — the choice page (generate / accept).
// `/pair/invite` — the invite-creation screen.
//
// Implemented via a single component that takes a `mode` prop so the
// App.tsx routing can switch on the path.
//
// **State-aware since W39.22.** Both routes are the *unpaired* flow, but the
// app shell's 配對 nav row points at `/pair` for everybody (moduleManifests
// line 88), so a user who is already paired reached the onboarding step-2
// screen: hero 「建立你們的空間」, a 產生邀請碼 card, and an invite-code box.
// Submitting a code there is answered by the backend's `AlreadyInPair` (measured:
// 409 `ALREADY_IN_PAIR` 「你已在 Pair 中」) — a message that is half English and
// contradicts the page it was typed into. The backend was always right to
// refuse; what was wrong is that the app offered the form at all.
//
// A paired user is sent to `/settings/pair`, which is where the pair editor
// already lives. The unpaired path is untouched, so onboarding still works.

import { Navigate } from "react-router-dom";

import { PairChoicePage } from "@/components/pair/PairChoicePage";
import { PairInvitePage } from "@/components/pair/PairInvitePage";
import { useAuth } from "@/lib/auth/AuthProvider";

interface PairPageProps {
  invite?: boolean;
}

export function PairPage({ invite = false }: PairPageProps = {}) {
  const { pairId, status } = useAuth();

  // While the session probe is still running, `pairId` is null for a paired
  // user too. Redirecting on that would bounce them to /settings/pair and back
  // on every cold load, so wait for a resolved status first.
  if (status === "authenticated" && pairId) {
    return <Navigate to="/settings/pair" replace />;
  }

  return invite ? <PairInvitePage /> : <PairChoicePage />;
}
